/**
 * DraggablePanel Component - Wrapper making panels draggable and resizable
 * Provides drag handle, resize handles, and titlebar controls
 */

import React, { useRef, useCallback, useState, useEffect } from 'react';
import { CANVAS_THEME } from './canvas-theme';
import {
  getResizeHandle,
  calculateNewSize,
  calculateNewPosition,
  getCursorForHandle,
  snapPositionToGrid,
  snapSizeToGrid,
  constrainToViewport,
} from './grid-utils';
import type { PanelType } from './panel-layout-store';

export interface DraggablePanelProps {
  id: string;
  type: PanelType;
  position: { x: number; y: number };
  size: { width: number; height: number };
  zIndex: number;
  minimized: boolean;
  gridSize: number;
  onMove: (id: string, newPos: { x: number; y: number }) => void;
  onResize: (id: string, newSize: { width: number; height: number }) => void;
  onClose: (id: string) => void;
  onMinimize: (id: string) => void;
  onBringToFront: (id: string) => void;
  title?: string;
  badgeCount?: number;
  children: React.ReactNode;
}

/**
 * DraggablePanel Component
 * Features:
 * - Draggable by titlebar with grid snapping
 * - 8-direction resizable with handles
 * - Minimize/maximize button
 * - Close button
 * - Click to bring to front
 * - Glassmorphic styling
 */
export function DraggablePanel({
  id,
  type,
  position,
  size,
  zIndex,
  minimized,
  gridSize,
  onMove,
  onResize,
  onClose,
  onMinimize,
  onBringToFront,
  title,
  badgeCount,
  children,
}: DraggablePanelProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const titlebarRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [cursor, setCursor] = useState<string>('default');
  const [isStamped, setIsStamped] = useState(false);

  /**
   * Handle titlebar drag start
   */
  const handleMouseDownDrag = useCallback(
    (e: React.MouseEvent) => {
      if (!titlebarRef.current) return;

      // Stamped panels cannot be moved
      if (isStamped) return;

      // Bring to front on drag
      onBringToFront(id);

      e.preventDefault();
      setIsDragging(true);

      const startX = e.clientX;
      const startY = e.clientY;
      const startPos = position;

      const handleMouseMove = (moveEvent: MouseEvent) => {
        const deltaX = moveEvent.clientX - startX;
        const deltaY = moveEvent.clientY - startY;

        const newPos = {
          x: startPos.x + deltaX,
          y: startPos.y + deltaY,
        };

        // Constrain position to viewport
        const constrainedPos = constrainToViewport(
          newPos,
          size,
          window.innerWidth,
          window.innerHeight
        );

        onMove(id, constrainedPos);
      };

      const handleMouseUp = () => {
        setIsDragging(false);
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [id, position, size, onMove, onBringToFront, isStamped]
  );

  /**
   * Handle resize start
   */
  const handleMouseDownResize = useCallback(
    (e: React.MouseEvent) => {
      if (!panelRef.current) return;

      // Stamped panels cannot be resized
      if (isStamped) return;

      // Bring to front on resize
      onBringToFront(id);

      e.preventDefault();
      setIsResizing(true);

      const startX = e.clientX;
      const startY = e.clientY;
      const startSize = size;
      const startPos = position;
      const panelRect = panelRef.current.getBoundingClientRect();
      const handle = getResizeHandle(startX, startY, panelRect);

      if (!handle) return;

      const handleMouseMove = (moveEvent: MouseEvent) => {
        const deltaX = moveEvent.clientX - startX;
        const deltaY = moveEvent.clientY - startY;

        const newSize = calculateNewSize(startSize, handle, deltaX, deltaY);
        const newPos = calculateNewPosition(startPos, handle, deltaX, deltaY);

        // Constrain position and size to viewport
        const constrainedPos = constrainToViewport(
          newPos,
          newSize,
          window.innerWidth,
          window.innerHeight
        );

        onResize(id, newSize);
        if (handle.includes('w') || handle.includes('n')) {
          onMove(id, constrainedPos);
        }
      };

      const handleMouseUp = () => {
        setIsResizing(false);
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [id, size, position, onResize, onMove, onBringToFront]
  );

  /**
   * Update cursor based on mouse position over resize handles
   */
  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!panelRef.current || isDragging || isResizing) return;

      const panelRect = panelRef.current.getBoundingClientRect();
      const handle = getResizeHandle(e.clientX, e.clientY, panelRect);
      setCursor(getCursorForHandle(handle));
    },
    [isDragging, isResizing]
  );

  const handleMouseLeave = useCallback(() => {
    if (!isDragging && !isResizing) {
      setCursor('default');
    }
  }, [isDragging, isResizing]);

  // Stamped panels have lowest z-index and solid appearance (planted into floor)
  const stampedZIndex = isStamped ? 10 : zIndex; // 10 is lowest possible for panels
  const stampedOpacity = isStamped ? 1.0 : 1.0; // Stamped panels are fully opaque (planted)
  const stampedBorder = isStamped
    ? '2px dashed rgba(0, 200, 100, 0.4)' // Green dashed border when stamped
    : CANVAS_THEME.panelBorder;
  const stampedBackground = isStamped
    ? 'rgba(255, 255, 255, 0.75)' // Denser glass when stamped (planted)
    : CANVAS_THEME.panelBackground;

  return (
    <div
      ref={panelRef}
      className="draggable-panel"
      data-tour={`panel-${type}`}
      style={{
        position: 'fixed',
        left: `${position.x}px`,
        top: `${position.y}px`,
        width: minimized ? '140px' : `${size.width}px`,
        height: minimized ? '40px' : `${size.height}px`,
        zIndex: stampedZIndex,
        display: 'flex',
        flexDirection: 'column',
        background: stampedBackground,
        backdropFilter: CANVAS_THEME.panelBackdrop,
        border: stampedBorder,
        borderRadius: CANVAS_THEME.panelBorderRadius,
        boxShadow: isStamped ? '0 2px 8px rgba(31, 38, 135, 0.04)' : CANVAS_THEME.panelShadow, // Lighter indigo shadow when stamped
        opacity: stampedOpacity,
        transition: isDragging || isResizing ? 'none' : `all ${CANVAS_THEME.transitionShift}`,
        cursor,
        userSelect: isDragging ? 'none' : 'auto',
        overflow: 'hidden',
      }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onClick={() => onBringToFront(id)}
    >
      {/* Titlebar - Hidden when stamped */}
      {!isStamped && (
        <div
          ref={titlebarRef}
          className="panel-titlebar"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: minimized
              ? `6px ${CANVAS_THEME.titlePaddingX}`
              : `${CANVAS_THEME.titlePaddingY} ${CANVAS_THEME.titlePaddingX}`,
            background: CANVAS_THEME.panelTitleBackground,
            borderBottom: '1px solid rgba(255, 255, 255, 0.12)', // Ultra-subtle divider
            cursor: 'move',
            flexShrink: 0,
            userSelect: 'none',
            gap: minimized ? '4px' : '8px',
          }}
          onMouseDown={handleMouseDownDrag}
        >
          <span
            style={{
              color: CANVAS_THEME.textPrimary,
              fontWeight: '500', // Lighter font weight for minimal feel
              fontSize: minimized ? '11px' : '13px', // Slightly smaller
              flex: 1,
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              opacity: 0.9, // Subtle opacity for minimal aesthetic
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            {title || type}
            {badgeCount !== undefined && badgeCount > 0 && (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '16px',
                  height: '16px',
                  borderRadius: '50%',
                  background: '#f04747',
                  color: '#fff',
                  fontSize: '10px',
                  fontWeight: 'bold',
                  flexShrink: 0,
                  lineHeight: 1,
                }}
              >
                {badgeCount > 9 ? '9+' : badgeCount}
              </span>
            )}
          </span>

          <div
            style={{
              display: 'flex',
              gap: minimized ? '3px' : '6px',
              flexShrink: 0,
              marginLeft: 'auto',
              alignItems: 'center',
            }}
          >
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsStamped(!isStamped);
              }}
              title={
                isStamped ? 'Unplant from floor (back to front)' : 'Stamp into floor (send to back)'
              }
              style={{
                padding: minimized ? '2px 5px' : '3px 6px',
                background: isStamped
                  ? 'rgba(0, 200, 100, 0.25)' // More visible when stamped
                  : 'rgba(102, 126, 234, 0.12)',
                border: isStamped
                  ? '1px solid rgba(0, 200, 100, 0.4)' // Green border when stamped
                  : '1px solid rgba(102, 126, 234, 0.2)',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: minimized ? '10px' : '11px',
                color: isStamped ? '#10b981' : CANVAS_THEME.accentColor,
                fontWeight: isStamped ? '600' : '400', // Bold when stamped
                transition: `all ${CANVAS_THEME.transitionShift}`,
                opacity: isStamped ? 1.0 : 0.8, // More visible when stamped
              }}
              onMouseEnter={(e) => {
                (e.target as HTMLButtonElement).style.background = isStamped
                  ? 'rgba(0, 200, 100, 0.35)'
                  : 'rgba(102, 126, 234, 0.2)';
                (e.target as HTMLButtonElement).style.opacity = '1';
                (e.target as HTMLButtonElement).style.transform = 'scale(1.1)';
              }}
              onMouseLeave={(e) => {
                (e.target as HTMLButtonElement).style.background = isStamped
                  ? 'rgba(0, 200, 100, 0.25)'
                  : 'rgba(102, 126, 234, 0.12)';
                (e.target as HTMLButtonElement).style.opacity = isStamped ? '1.0' : '0.8';
                (e.target as HTMLButtonElement).style.transform = 'scale(1)';
              }}
            >
              {isStamped ? '📍' : '☁️'}
            </button>

            <button
              onClick={(e) => {
                e.stopPropagation();
                onMinimize(id);
              }}
              title={minimized ? 'Maximize' : 'Minimize'}
              style={{
                padding: minimized ? '2px 5px' : '3px 6px',
                background: 'rgba(0, 0, 0, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: minimized ? '10px' : '11px',
                color: CANVAS_THEME.textPrimary,
                transition: `all ${CANVAS_THEME.transitionShift}`,
                opacity: 0.7,
              }}
              onMouseEnter={(e) => {
                (e.target as HTMLButtonElement).style.background = 'rgba(0, 0, 0, 0.1)';
                (e.target as HTMLButtonElement).style.opacity = '1';
              }}
              onMouseLeave={(e) => {
                (e.target as HTMLButtonElement).style.background = 'rgba(0, 0, 0, 0.05)';
                (e.target as HTMLButtonElement).style.opacity = '0.7';
              }}
            >
              {minimized ? '□' : '−'}
            </button>

            <button
              onClick={(e) => {
                e.stopPropagation();
                onClose(id);
              }}
              title="Close"
              style={{
                padding: minimized ? '2px 5px' : '3px 6px',
                background: 'rgba(239, 68, 68, 0.08)',
                border: '1px solid rgba(239, 68, 68, 0.2)',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: minimized ? '10px' : '11px',
                color: '#ef4444',
                transition: `all ${CANVAS_THEME.transitionShift}`,
                opacity: 0.7,
              }}
              onMouseEnter={(e) => {
                (e.target as HTMLButtonElement).style.background = 'rgba(239, 68, 68, 0.15)';
                (e.target as HTMLButtonElement).style.opacity = '1';
              }}
              onMouseLeave={(e) => {
                (e.target as HTMLButtonElement).style.background = 'rgba(239, 68, 68, 0.08)';
                (e.target as HTMLButtonElement).style.opacity = '0.7';
              }}
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* Floating Unstamp Button - Only shown when stamped, upper right */}
      {isStamped && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            setIsStamped(false);
          }}
          title="Unplant from floor (back to front)"
          style={{
            position: 'absolute',
            top: '8px',
            right: '8px',
            zIndex: 100,
            padding: '6px 10px',
            background: 'rgba(0, 200, 100, 0.3)',
            border: '1px solid rgba(0, 200, 100, 0.5)',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '18px',
            color: '#10b981',
            fontWeight: '600',
            transition: `all ${CANVAS_THEME.transitionShift}`,
            opacity: 1.0,
          }}
          onMouseEnter={(e) => {
            (e.target as HTMLButtonElement).style.background = 'rgba(0, 200, 100, 0.4)';
            (e.target as HTMLButtonElement).style.transform = 'scale(1.15)';
          }}
          onMouseLeave={(e) => {
            (e.target as HTMLButtonElement).style.background = 'rgba(0, 200, 100, 0.3)';
            (e.target as HTMLButtonElement).style.transform = 'scale(1)';
          }}
        >
          📍
        </button>
      )}

      {/* Content - Minimal padding for compact design */}
      {!minimized && (
        <div
          style={{
            flex: 1,
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column',
            padding: `${CANVAS_THEME.panelPaddingY} ${CANVAS_THEME.panelPaddingX}`,
          }}
        >
          {children}
        </div>
      )}

      {/* Resize Handles - positioned inside panel at edges */}
      {!minimized && (
        <>
          {/* Top-left corner */}
          <div
            style={{
              position: 'absolute',
              width: '12px',
              height: '12px',
              top: '0',
              left: '0',
              cursor: 'nwse-resize',
              zIndex: 1000,
            }}
            onMouseDown={handleMouseDownResize}
          />
          {/* Top-right corner */}
          <div
            style={{
              position: 'absolute',
              width: '12px',
              height: '12px',
              top: '0',
              right: '0',
              cursor: 'nesw-resize',
              zIndex: 1000,
            }}
            onMouseDown={handleMouseDownResize}
          />
          {/* Bottom-left corner */}
          <div
            style={{
              position: 'absolute',
              width: '12px',
              height: '12px',
              bottom: '0',
              left: '0',
              cursor: 'nesw-resize',
              zIndex: 1000,
            }}
            onMouseDown={handleMouseDownResize}
          />
          {/* Bottom-right corner */}
          <div
            style={{
              position: 'absolute',
              width: '12px',
              height: '12px',
              bottom: '0',
              right: '0',
              cursor: 'nwse-resize',
              zIndex: 1000,
            }}
            onMouseDown={handleMouseDownResize}
          />

          {/* Top edge */}
          <div
            style={{
              position: 'absolute',
              width: 'calc(100% - 24px)',
              height: '4px',
              top: '0',
              left: '12px',
              cursor: 'ns-resize',
              zIndex: 1000,
            }}
            onMouseDown={handleMouseDownResize}
          />
          {/* Right edge */}
          <div
            style={{
              position: 'absolute',
              width: '4px',
              height: 'calc(100% - 24px)',
              right: '0',
              top: '12px',
              cursor: 'ew-resize',
              zIndex: 1000,
            }}
            onMouseDown={handleMouseDownResize}
          />
          {/* Bottom edge */}
          <div
            style={{
              position: 'absolute',
              width: 'calc(100% - 24px)',
              height: '4px',
              bottom: '0',
              left: '12px',
              cursor: 'ns-resize',
              zIndex: 1000,
            }}
            onMouseDown={handleMouseDownResize}
          />
          {/* Left edge */}
          <div
            style={{
              position: 'absolute',
              width: '4px',
              height: 'calc(100% - 24px)',
              left: '0',
              top: '12px',
              cursor: 'ew-resize',
              zIndex: 1000,
            }}
            onMouseDown={handleMouseDownResize}
          />
        </>
      )}
    </div>
  );
}
